import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { company } from "../mock";
import { SectionLabel } from "../components/common";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", subject: "", message: "" };

const ContactSection = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete the required fields", description: "Name, email and message are required." });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast({ title: "Enquiry sent", description: "Thank you — the Salmons Brothers team will be in touch shortly." });
      setForm(empty);
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call us directly." });
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: Phone, label: "Telephone", value: company.phone, href: company.phoneHref },
    { icon: Mail, label: "Email", value: company.email, href: `mailto:${company.email}` },
    { icon: MapPin, label: "Address", value: company.address },
  ];

  return (
    <section className="py-24 bg-[#F1F3F7]">
      <div className="max-w-[1320px] mx-auto px-5 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <SectionLabel>Our Contact</SectionLabel>
          <h2 className="section-heading text-[#0B102D] text-4xl md:text-5xl mb-6">
            Get specialist advice for your construction project
          </h2>
          <p className="text-gray-600 leading-relaxed mb-9">
            No job is too small and the customer always comes first. Get in touch
            with our friendly, family-run team for a no-obligation chat about your
            project.
          </p>
          <div className="space-y-5">
            {info.map((it) => {
              const Icon = it.icon;
              const content = (
                <div className="flex items-center gap-4">
                  <span className="w-14 h-14 shrink-0 flex items-center justify-center rounded-full bg-[#CD0008] text-white">
                    <Icon size={22} />
                  </span>
                  <div>
                    <span className="block text-xs uppercase tracking-wide text-gray-500">{it.label}</span>
                    <span className="font-display font-bold text-[#0B102D]">{it.value}</span>
                  </div>
                </div>
              );
              return it.href ? (
                <a key={it.label} href={it.href} className="block hover:opacity-80 transition-opacity">{content}</a>
              ) : (
                <div key={it.label}>{content}</div>
              );
            })}
          </div>
        </div>

        <form onSubmit={onSubmit} className="bg-white rounded-lg p-8 md:p-10 shadow-sm">
          <h3 className="font-display font-bold text-[#0B102D] text-2xl mb-6">Send your message</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <input name="name" value={form.name} onChange={onChange} placeholder="Your Name *" className="border border-gray-200 rounded-md px-4 py-3.5 text-sm focus:outline-none focus:border-[#CD0008] focus:ring-1 focus:ring-[#CD0008] transition-colors" />
            <input name="email" type="email" value={form.email} onChange={onChange} placeholder="Your Email *" className="border border-gray-200 rounded-md px-4 py-3.5 text-sm focus:outline-none focus:border-[#CD0008] focus:ring-1 focus:ring-[#CD0008] transition-colors" />
            <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone Number" className="border border-gray-200 rounded-md px-4 py-3.5 text-sm focus:outline-none focus:border-[#CD0008] focus:ring-1 focus:ring-[#CD0008] transition-colors" />
            <input name="subject" value={form.subject} onChange={onChange} placeholder="Subject" className="border border-gray-200 rounded-md px-4 py-3.5 text-sm focus:outline-none focus:border-[#CD0008] focus:ring-1 focus:ring-[#CD0008] transition-colors" />
          </div>
          <textarea name="message" value={form.message} onChange={onChange} rows={5} placeholder="Tell us about your project *" className="w-full mt-4 border border-gray-200 rounded-md px-4 py-3.5 text-sm focus:outline-none focus:border-[#CD0008] focus:ring-1 focus:ring-[#CD0008] transition-colors resize-none" />
          <button type="submit" disabled={loading} className="mt-5 inline-flex items-center gap-2 bg-[#CD0008] hover:bg-[#A80006] disabled:opacity-70 text-white font-display font-semibold uppercase tracking-wide text-sm px-8 py-4 rounded-sm transition-colors">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {loading ? "Sending..." : "Send Your Message"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
